/**
 * @fileoverview Advanced language detection utility
 * Provides functionality to detect various languages from text input
 */

export type SupportedLanguage = 'en' | 'ar' | 'hi' | 'unknown';

// Arabic character range (Arabic, Persian, Urdu)
const ARABIC_PATTERN = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g;

// Devanagari character range (Hindi, Sanskrit, Marathi, etc.)
const DEVANAGARI_PATTERN = /[\u0900-\u097F]/g;

// Helper function to calculate character ratio
const calculateRatio = (text: string, pattern: RegExp): number => {
  const matches = text.match(pattern);
  if (!matches) return 0;
  return matches.length / text.length;
};

/**
 * Detects the language of the provided text
 * Currently supports English, Arabic, and Hindi
 * 
 * @param text - Text to analyze for language
 * @param threshold - Minimum ratio of characters to determine language (default: 0.3)
 * @returns Detected language code or 'unknown' if no match
 */
export function detectLanguage(text: string, threshold: number = 0.3): SupportedLanguage {
  if (!text || text.trim().length === 0) return 'unknown';
  
  const normalizedText = text.trim();
  
  // Check Arabic
  const arabicRatio = calculateRatio(normalizedText, ARABIC_PATTERN);
  if (arabicRatio > threshold) return 'ar';
  
  // Check Hindi (Devanagari)
  const devanagariRatio = calculateRatio(normalizedText, DEVANAGARI_PATTERN);
  if (devanagariRatio > threshold) return 'hi';
  
  // Default to English for Latin script or if no other match
  return 'en';
}

/**
 * Determines if text is primarily in Arabic
 *
 * @param text - Text to analyze
 * @param threshold - Minimum ratio threshold (default: 0.3)
 * @returns Whether text is primarily Arabic
 */
export function isArabicText(text: string, threshold: number = 0.3): boolean {
  return detectLanguage(text, threshold) === 'ar';
}

/**
 * Determines if text is primarily in Hindi
 *
 * @param text - Text to analyze
 * @param threshold - Minimum ratio threshold (default: 0.3)
 * @returns Whether text is primarily Hindi
 */
export function isHindiText(text: string, threshold: number = 0.3): boolean {
  return detectLanguage(text, threshold) === 'hi';
}

/**
 * Gets the appropriate font family based on detected language
 * 
 * @param text - Text to analyze
 * @returns CSS variable for the appropriate font family
 */
export function getFontFamilyForText(text: string): string {
  const lang = detectLanguage(text);
  switch (lang) {
    case 'ar':
      return 'var(--font-family-arabic)';
    case 'hi':
      return 'var(--font-family-hindi, var(--font-family-english))';
    case 'en':
    default:
      return 'var(--font-family-english)';
  }
}